import { ApolloClient, createHttpLink, InMemoryCache, from, split } from '@apollo/client/core'
import { onError } from '@apollo/client/link/error'
import { setContext } from '@apollo/client/link/context'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { getMainDefinition } from '@apollo/client/utilities'
import { createClient } from 'graphql-ws'
import { Kind, OperationTypeNode } from 'graphql'
import { getAuth } from '@firebase/auth'
import { useVoterPins } from './hooks/useVoterPins'
import useNotifications from './hooks/useNotifications'

const httpLink = createHttpLink({
  uri: import.meta.env.VITE_GRAPHQL_URL,
  includeExtensions: true,
})

const voterPins = useVoterPins()

// The same credential rules the HTTP auth link uses: a voter authenticates with
// Basic `voter-id:pin` when the `voter-id` query param is present, otherwise a
// Firebase Bearer id token. Shared with the WebSocket connectionParams below.
async function getAuthorization (): Promise<string> {
  const voterId = new URLSearchParams(window.location.search).get('voter-id')
  if (voterId) {
    return `Basic ${btoa(`${voterId}:${voterPins.value[voterId] ?? ''}`)}`
  }
  const token = await getAuth().currentUser?.getIdToken()
  return token ? `Bearer ${token}` : ''
}

const authLink = setContext(async (_, { headers }: { headers?: Record<string, string> }) => ({
  headers: {
    ...headers,
    authorization: await getAuthorization(),
  },
}))

// Subscriptions travel over a graphql-ws WebSocket. `lazy` opens the socket only
// while a subscription is active; graphql-ws reconnects automatically when the
// socket drops (e.g. Cloud Run's max request time) and re-sends fresh
// credentials via connectionParams on every (re)connect.
const wsLink = new GraphQLWsLink(
  createClient({
    url: import.meta.env.VITE_GRAPHQL_WS_URL ?? import.meta.env.VITE_GRAPHQL_URL.replace(/^http/, 'ws'),
    lazy: true,
    lazyCloseTimeout: 20_000,
    connectionParams: async () => ({
      authorization: await getAuthorization(),
    }),
  })
)

const { push: pushError } = useNotifications()
// `graphQLErrors` and `networkError` are the correct `onError` API in Apollo
// Client 3.x (this project pins ^3.14.0); they are only consolidated into a
// single `error` property in Apollo Client 4, which the no-deprecated rule
// flags prematurely.
// eslint-disable-next-line @typescript-eslint/no-deprecated
const errorLink = onError(({ graphQLErrors, networkError }) => {
  const voterId = new URLSearchParams(window.location.search).get('voter-id')
  if (voterId && graphQLErrors?.find(err => err.extensions?.failType === 'VOTER_AUTH_FAIL')) {
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete voterPins.value[voterId]
    pushError({
      message: 'Authentication Error: Your Voter PIN might be wrong, or this voter/election doesn\'t exist',
      type: 'server',
      code: 'VOTER_AUTH_FAIL',
    })
    return
  }

  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      console.log({ ...err })
      pushError({
        message: err.message,
        type: 'server',
        code: typeof err.extensions?.code === 'string' ? err.extensions.code : undefined,
      })
    }
  }
  if (networkError) {
    pushError({
      message: networkError.message,
      type: 'network',
    })
  }
})

const cache = new InMemoryCache({
  typePolicies: {
    Motion: {
      fields: {
        excludedVoters: {
          merge: false,
        },
      },
    },
    Vote: {
      fields: {
        history: {
          merge: false,
        },
      },
    },
    Election: {
      fields: {
        motions: {
          merge: false,
        },
        admins: {
          merge: false,
        },
        voters: {
          merge: false,
        },
      },
    },
    BillingAccount: {
      fields: {
        admins: {
          merge: false,
        },
        users: {
          merge: false,
        },
        transactions: {
          merge: false,
        },
      },
    },
  },
})

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query)
    return (
      definition.kind === Kind.OPERATION_DEFINITION &&
      definition.operation === OperationTypeNode.SUBSCRIPTION
    )
  },
  wsLink,
  from([errorLink, authLink, httpLink])
)

export const apolloClient = new ApolloClient({
  link: splitLink,
  cache,
})
